import './ScheduleWindow.scss';

import { IconAlertInfoOutlined } from '@hulu-react-style-components/icons';
import type { SwitchChangeEventHandler } from 'antd/lib/switch';
import type { FieldHelperProps } from 'formik';
import { bem } from 'mission-control-common-components';
import React from 'react';

import DateInput from '../../../../../../common/DateInput';
import { useFieldFast } from '../../../../../../common/Form/hooks';
import PercentageInput from '../../../../../../common/PercentageInput';
import RemoveButton from '../../../../../../common/RemoveButton';
import ToggleSwitch from '../../../../../../common/ToggleSwitch';
import Tooltip from '../../../../../../common/Tooltip';
import {
  CUSTOM_PACING_INFO_TOOLTIPS,
  INVALID_DATE,
  MAX_HEAVY_UP_VALUE,
  REMOVE_LABEL,
  SCHEDULE_DATE_INPUT_STEP,
  SCHEDULE_END_INPUT_LABEL,
  SCHEDULE_HEAVY_UP_INPUT_LABEL,
  SCHEDULE_START_INPUT_LABEL,
} from '../../constants';
import { CpScheduleFormFields } from '../../types';
import { getFormattedCPWindowDate } from '../../utils/form';

const [block, element] = bem('cp-schedule-input-window');

export const DYNAMIC_START_DATE_TOGGLE_LABEL = 'Dynamic start date';
export const DYNAMIC_END_DATE_TOGGLE_LABEL = 'Dynamic end date';

export const DYNAMIC_START_DATE_TOGGLE_TOOLTIP_TEXT =
  "Switch toggle ON to dynamically set the custom pacing schedule start date to the line item's start date.";
export const DYNAMIC_END_DATE_TOGGLE_TOOLTIP_TEXT =
  "Switch toggle ON to dynamically set the custom pacing schedule end date to the line item's end date.";

export const DYNAMIC_START_DATE_LABEL = 'Start date of the line item';
export const DYNAMIC_END_DATE_LABEL = 'End date of the line item';

type ScheduleWindowProps = {
  index: number;
  isDisabled: boolean;
  onResetErrors: () => void;
  onRemove?: () => void;
  hasDynamicEndDate: boolean;
  hasDynamicStartDate: boolean;
  isDynamicStartDateEnabled: boolean;
  isDynamicEndDateEnabled: boolean;
  handleChangeIsDynamicStartDate: SwitchChangeEventHandler;
  handleChangeIsDynamicEndDate: SwitchChangeEventHandler;
  isDynamicStartDateSwitchDisabled: boolean;
  isDynamicEndDateSwitchDisabled: boolean;
};

const ScheduleWindow = ({
  index,
  isDisabled,
  onResetErrors,
  onRemove,
  hasDynamicStartDate,
  hasDynamicEndDate,
  isDynamicEndDateEnabled,
  isDynamicStartDateEnabled,
  handleChangeIsDynamicEndDate,
  handleChangeIsDynamicStartDate,
  isDynamicStartDateSwitchDisabled,
  isDynamicEndDateSwitchDisabled,
}: ScheduleWindowProps): React.JSX.Element => {
  const [startField, startMeta, startHelpers] = useFieldFast<string | null>(
    `${CpScheduleFormFields.Ranges}[${index}].start`
  );
  const [endField, endMeta, endHelpers] = useFieldFast<string | null>(`${CpScheduleFormFields.Ranges}[${index}].end`);
  const [heavyUpField, heavyUpMeta, heavyUpHelpers] = useFieldFast<number | null>(
    `${CpScheduleFormFields.Ranges}[${index}].heavyUp`
  );

  const handleChange = (helpers: FieldHelperProps<string | null>) => (value: string): void => {
    const transformedValue: string = getFormattedCPWindowDate(value);

    const dateValue = new Date(transformedValue);
    const isValidDate = !isNaN(dateValue.getTime());

    if (isValidDate) {
      helpers.setValue(transformedValue);
      onResetErrors();
    } else {
      helpers.setValue(null);
      helpers.setError(INVALID_DATE);
    }
  };

  const handleBlur = (value: string | null, helpers: FieldHelperProps<string | null>) => (): void => {
    if (!value) helpers.setError(INVALID_DATE);
  };

  const handleHeavyUpChange = (value: number | null): void => {
    heavyUpHelpers.setValue(value);
    heavyUpMeta.error && heavyUpHelpers.setError(undefined);
  };

  return (
    <tr className={block()} data-testid="schedule-window">
      <td className={element('schedule-start')}>
        <div className={element('field-title')}>
          {SCHEDULE_START_INPUT_LABEL}
          <Tooltip id={`schedule-start-${index}-tooltip`} message={CUSTOM_PACING_INFO_TOOLTIPS.START_DATE} place="top">
            <IconAlertInfoOutlined
              className="info-icon"
              aria-label={`schedule-start-${index}-info-icon`}
              data-testid={`schedule-start-${index}-info-icon`}
            />
          </Tooltip>
        </div>
        <div className={element('date-input-wrapper')}>
          {hasDynamicStartDate && isDynamicStartDateEnabled ? (
            <div className={element('dynamic-date-label')}>{DYNAMIC_START_DATE_LABEL}</div>
          ) : (
            <DateInput
              value={startField.value}
              type="datetime-local"
              onChange={handleChange(startHelpers)}
              error={!!startMeta.error}
              onBlur={handleBlur(startField.value, startHelpers)}
              step={SCHEDULE_DATE_INPUT_STEP}
              disabled={isDisabled}
            />
          )}
        </div>
        {startMeta.error && <p className={element('error-message')}>{startMeta.error}</p>}
        {hasDynamicStartDate && (
          <div className={element('dynamic-date-wrapper')}>
            <Tooltip id="dynamic-start-date-disabled-tooltip" message={DYNAMIC_START_DATE_TOGGLE_TOOLTIP_TEXT}>
              <ToggleSwitch
                isOn={isDynamicStartDateEnabled}
                onChange={handleChangeIsDynamicStartDate}
                className={element('dynamic-date-switch')}
                disabled={isDynamicStartDateSwitchDisabled}
              />
            </Tooltip>
            <label className={element('dynamic-date-toggle-label')}>{DYNAMIC_START_DATE_TOGGLE_LABEL}</label>
          </div>
        )}
      </td>

      <td className={element('schedule-end')}>
        <div className={element('field-title')}>
          {SCHEDULE_END_INPUT_LABEL}
          <Tooltip id={`schedule-end-${index}-tooltip`} message={CUSTOM_PACING_INFO_TOOLTIPS.END_DATE} place="top">
            <IconAlertInfoOutlined
              className="info-icon"
              aria-label={`schedule-end-${index}-info-icon`}
              data-testid={`schedule-end-${index}-info-icon`}
            />
          </Tooltip>
        </div>
        <div className={element('date-input-wrapper')}>
          {hasDynamicEndDate && isDynamicEndDateEnabled ? (
            <div className={element('dynamic-date-label')}>{DYNAMIC_END_DATE_LABEL}</div>
          ) : (
            <DateInput
              value={endField.value}
              type="datetime-local"
              onChange={handleChange(endHelpers)}
              error={!!endMeta.error}
              onBlur={handleBlur(endField.value, endHelpers)}
              step={SCHEDULE_DATE_INPUT_STEP}
              disabled={isDisabled}
            />
          )}
        </div>
        {endMeta.error && <p className={element('error-message')}>{endMeta.error}</p>}
        {hasDynamicEndDate && (
          <div className={element('dynamic-date-wrapper')}>
            <Tooltip id="dynamic-end-date-disabled-tooltip" message={DYNAMIC_END_DATE_TOGGLE_TOOLTIP_TEXT}>
              <ToggleSwitch
                isOn={isDynamicEndDateEnabled}
                onChange={handleChangeIsDynamicEndDate}
                className={element('dynamic-date-switch')}
                disabled={isDynamicEndDateSwitchDisabled}
              />
            </Tooltip>
            <label className={element('dynamic-date-toggle-label')}>{DYNAMIC_END_DATE_TOGGLE_LABEL}</label>
          </div>
        )}
      </td>

      <td className={element('schedule-heavy-up')}>
        <div className={element('field-title')}>
          {SCHEDULE_HEAVY_UP_INPUT_LABEL}{' '}
          <Tooltip id={`schedule-heavy-up-${index}-tooltip`} message={CUSTOM_PACING_INFO_TOOLTIPS.HEAVY_UP} place="top">
            <IconAlertInfoOutlined
              className="info-icon"
              aria-label={`heavy-up-${index}-info-icon`}
              data-testid={`heavy-up-${index}-info-icon`}
            />
          </Tooltip>
        </div>
        <div className={element('heavy-up-wrapper')}>
          <PercentageInput
            value={heavyUpField.value}
            onChange={handleHeavyUpChange}
            icon={<span>%</span>}
            error={!!heavyUpMeta.error}
            disabled={isDisabled}
            maxValue={MAX_HEAVY_UP_VALUE}
          />
        </div>
        {heavyUpMeta.error && <p className={element('error-message')}>{heavyUpMeta.error}</p>}
      </td>

      {onRemove && (
        <td className={element('remove-button')}>
          <RemoveButton arialLabel={REMOVE_LABEL} onClick={onRemove} />
        </td>
      )}
    </tr>
  );
};

export default ScheduleWindow;
